import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedScene, SceneInput, SceneResult } from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'
import { useSettingsStore } from './settingsStore'
import { getCurrentGeneratorModel } from '../lib/generatorModelSelection'

// シーンチェーン（連続したシーンのグループ）
export interface SceneChain {
  id: string
  name: string
  sceneIds: string[]
  createdAt: string
  updatedAt: string
  lastSettings?: {  // 最後に使用した設定を保存
    continuationType?: 'direct' | 'timeskip' | 'perspective' | 'parallel'
    genre?: string
    customGenre?: string
    perspective?: string
    writingStyle?: string
    customStyle?: string
    creativityLevel?: number
    useProMode?: boolean
    avoidElements?: string[]
  }
}

// 章セクション管理（長編対応）
interface ChapterSection {
  id: string
  chapterTitle: string  // 章のタイトル
  chapterNumber: number  // 章番号
  plotId?: string  // 関連するプロットID
  plotChapterNumber?: number  // プロット内の章番号
  workId?: string  // 作品を識別するID（plotIdがない場合のフォールバック）
  targetLength: number  // 目標文字数（例: 20000）
  sections: SectionInfo[]  // セクション情報
  createdAt: string
  updatedAt: string
  // 章要約機能（超長編対応）
  chapterSummary?: ChapterSummary  // この章の要約
  previousChaptersSummary?: string  // 前章までの累積要約（4000文字以上）
  isCompleted?: boolean  // 章が完了したか
  // 前章との引き継ぎ情報
  previousChapterFullText?: string  // 前章の最終セクションの本文（自然な流れのため）
  cumulativeSummary?: string  // ここまでの全体の流れの要約（伏線含む）
  previousChapterConnection?: string  // 前章との繋がりの説明（章要約で生成）
  nextChapterHints?: string  // 次章への布石・伏線（章要約で生成）
  avoidWords?: string[]  // 禁止ワード・表現のリスト
}

// 章要約の詳細情報
interface ChapterSummary {
  detailedSummary: string  // 詳細な要約（2000文字程度）
  keyEvents: string[]  // 重要な出来事
  characterDevelopments: string[]  // キャラクターの変化・成長
  plotProgressions: string[]  // ストーリーの進展
  unresolved: string[]  // 未解決の伏線・課題
  worldBuilding: string[]  // 世界観の拡張要素
  emotionalBeats: string[]  // 感情的な転換点
  generatedAt: string  // 要約生成日時
}

interface SectionInfo {
  sectionNumber: number  // セクション番号（1, 2, 3...）
  sceneId?: string  // 生成されたシーンのID
  status: 'pending' | 'in_progress' | 'completed' | 'revision'  // ステータス
  targetLength: number  // このセクションの目標文字数
  actualLength?: number  // 実際の文字数
  summary?: string  // セクションの概要
  notes?: string  // メモ・修正内容
  fullText?: string  // セクションの本文（シーンが削除されても保持）
  title?: string  // セクションのタイトル
}

interface SceneStore {
  scenes: SavedScene[]
  currentScene: SavedScene | null
  sceneChains: SceneChain[]  // 連続したシーンのグループ
  chapterSections: ChapterSection[]  // 章セクション管理（長編対応）
  
  // 基本操作
  addScene: (result: SceneResult, input: SceneInput) => SavedScene
  addBatchScenes: (scenes: Array<{ result: SceneResult; input: SceneInput }>) => void
  updateScene: (id: string, updates: Partial<SceneResult>) => void
  removeScene: (id: string) => void
  getScene: (id: string) => SavedScene | undefined
  setCurrentScene: (scene: SavedScene | null) => void
  
  // 連続シーン管理
  createSceneChain: (name: string, firstSceneId: string) => string
  addToChain: (chainId: string, sceneId: string, settings?: SceneChain['lastSettings']) => void
  removeFromChain: (chainId: string, sceneId: string) => void
  deleteChain: (chainId: string) => void
  getChain: (chainId: string) => SceneChain | undefined
  getChainContextScenes: (chainId: string, currentSceneId: string, contextDepth?: number) => {
    fullScenes: SavedScene[]  // フルテキストを含む直前のシーン（最大contextDepth個）
    summarizedScenes: Array<{  // それ以前の要約されたシーン
      id: string
      title: string
      summary: string
    }>
    contextText: string  // 生成用の結合されたコンテキストテキスト
  }
  
  // 章セクション管理（長編対応）
  createChapterSection: (params: {
    chapterTitle: string
    chapterNumber: number
    targetLength: number
    sectionCount: number
    plotId?: string
    plotChapterNumber?: number
    workId?: string  // 作品を識別するID
    cumulativeSummary?: string  // 累積要約を外部から渡す場合
  }) => string
  updateSectionStatus: (chapterId: string, sectionNumber: number, status: SectionInfo['status'], sceneId?: string) => void
  updateSectionNotes: (chapterId: string, sectionNumber: number, notes: string) => void
  updateSectionSummary: (chapterId: string, sectionNumber: number, summary: string) => void
  updateSectionTargetLength: (chapterId: string, sectionNumber: number, targetLength: number) => void
  updateChapterAvoidWords: (chapterId: string, avoidWords: string[]) => void
  getChapterSection: (chapterId: string) => ChapterSection | undefined
  getNextPendingSection: (chapterId: string) => SectionInfo | undefined
  getCurrentOrNextSection: (chapterId: string) => SectionInfo | undefined
  deleteChapterSection: (chapterId: string) => void
  // 章要約機能
  generateChapterSummary: (chapterId: string) => Promise<void>
  resetChapterSummary: (chapterId: string) => void
  updateChapterSummary: (chapterId: string, summary: ChapterSummary) => void
  updatePreviousChaptersSummary: (chapterId: string, cumulativeSummary: string) => void
  markChapterAsCompleted: (chapterId: string) => void
  getCompletedChapters: (workId?: string) => ChapterSection[]
  getCumulativeSummary: (upToChapter: number, workId?: string) => string
  
  // 検索・フィルタ
  searchScenes: (query: string) => SavedScene[]
  filterByCharacter: (characterId: string) => SavedScene[]
  filterByLocation: (location: string) => SavedScene[]
  filterByWorld: (worldId: string) => SavedScene[]
  
  // エクスポート・インポート
  exportAsText: () => void
  exportAsJSON: () => void
  exportSingleAsText: (id: string) => void
  exportSingleAsJSON: (id: string) => void
  exportChainAsText: (chainId: string) => void
  exportChapterAsText: (chapterId: string) => void
  exportChapterAsJSON: (chapterId: string) => void
  importFromJSON: (jsonString: string) => void
  importChapterFromJSON: (jsonString: string) => boolean
  
  // ユーティリティ
  clearAll: () => void
  fixDuplicateIds: () => boolean
}

const MAX_SCENES = 500  // 最大500シーンの保存制限

export const useSceneStore = create<SceneStore>()(
  persist(
    (set, get) => ({
      scenes: [],
      currentScene: null,
      sceneChains: [],
      chapterSections: [],

      // 基本操作
      addScene: (result, input) => {
        const newScene: SavedScene = {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          input,
          result,
          timestamp: Date.now(),
          version: '1.0'
        }

        set(state => {
          const scenes = [newScene, ...state.scenes]
          if (scenes.length > MAX_SCENES) {
            scenes.pop()
          }
          return { scenes, currentScene: newScene }
        })
        
        // 新しいシーンを返す
        return newScene
      },
      
      addBatchScenes: (batchScenes) => {
        const newScenes: SavedScene[] = batchScenes.map((scene, index) => ({
          id: `${Date.now() + index}_${Math.random().toString(36).slice(2, 11)}`,
          input: scene.input,
          result: scene.result,
          timestamp: Date.now() + index,
          version: '1.0'
        }))

        set(state => {
          let scenes = [...newScenes, ...state.scenes]
          if (scenes.length > MAX_SCENES) {
            scenes = scenes.slice(0, MAX_SCENES)
          }
          return { scenes }
        })
      },

      updateScene: (id, updates) => {
        set(state => ({
          scenes: state.scenes.map(scene =>
            scene.id === id
              ? { ...scene, result: { ...scene.result, ...updates } }
              : scene
          ),
          currentScene: state.currentScene?.id === id
            ? { ...state.currentScene, result: { ...state.currentScene.result, ...updates } }
            : state.currentScene
        }))
      },

      removeScene: (id) => {
        set(state => {
          // シーンチェーンからも削除
          const updatedChains = state.sceneChains.map(chain => ({
            ...chain,
            sceneIds: chain.sceneIds.filter(sceneId => sceneId !== id)
          }))
          
          return {
            scenes: state.scenes.filter(scene => scene.id !== id),
            currentScene: state.currentScene?.id === id ? null : state.currentScene,
            sceneChains: updatedChains
          }
        })
      },

      getScene: (id) => {
        const scenes = get().scenes
        console.log(`【デバッグ】getScene呼び出し:`, {
          要求ID: id,
          保存済みシーン数: scenes.length,
          最初の3件のID: scenes.slice(0, 3).map(s => s.id)
        })
        const foundScene = scenes.find(scene => scene.id === id)
        if (foundScene) {
          console.log(`【デバッグ】シーン発見:`, {
            ID: foundScene.id,
            タイトル: foundScene.result?.title,
            保存時刻: new Date(foundScene.timestamp).toLocaleString()
          })
        } else {
          console.log(`【デバッグ】警告: ID ${id} のシーンが見つかりません`)
        }
        return foundScene
      },

      setCurrentScene: (scene) => {
        set({ currentScene: scene })
      },

      // 連続シーン管理
      createSceneChain: (name, firstSceneId) => {
        const newChain: SceneChain = {
          id: `chain_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name,
          sceneIds: [firstSceneId],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }

        set(state => ({
          sceneChains: [...state.sceneChains, newChain]
        }))
        
        return newChain.id
      },

      addToChain: (chainId, sceneId, settings) => {
        set(state => ({
          sceneChains: state.sceneChains.map(chain =>
            chain.id === chainId
              ? {
                  ...chain,
                  sceneIds: [...chain.sceneIds, sceneId],
                  updatedAt: new Date().toISOString(),
                  lastSettings: settings || chain.lastSettings
                }
              : chain
          )
        }))
      },

      removeFromChain: (chainId, sceneId) => {
        set(state => ({
          sceneChains: state.sceneChains.map(chain =>
            chain.id === chainId
              ? {
                  ...chain,
                  sceneIds: chain.sceneIds.filter(id => id !== sceneId),
                  updatedAt: new Date().toISOString()
                }
              : chain
          )
        }))
      },

      deleteChain: (chainId) => {
        set(state => ({
          sceneChains: state.sceneChains.filter(chain => chain.id !== chainId)
        }))
      },

      getChain: (chainId) => {
        return get().sceneChains.find(chain => chain.id === chainId)
      },
      
      getChainContextScenes: (chainId, currentSceneId, contextDepth = 4) => {
        const chain = get().sceneChains.find(c => c.id === chainId)
        const scenes = get().scenes
        
        if (!chain) {
          return {
            fullScenes: [],
            summarizedScenes: [],
            contextText: ''
          }
        }
        
        // 現在のシーンのインデックスを見つける
        const currentIndex = chain.sceneIds.indexOf(currentSceneId)
        if (currentIndex === -1) {
          return {
            fullScenes: [],
            summarizedScenes: [],
            contextText: ''
          }
        }
        
        // フルテキストを含むシーンの開始インデックス
        const fullTextStartIndex = Math.max(0, currentIndex - contextDepth + 1)
        
        // フルテキストシーンを取得
        const fullScenes: SavedScene[] = []
        for (let i = fullTextStartIndex; i <= currentIndex; i++) {
          const scene = scenes.find(s => s.id === chain.sceneIds[i])
          if (scene) {
            fullScenes.push(scene)
          }
        }
        
        // 要約が必要なシーンを取得
        const summarizedScenes: Array<{ id: string; title: string; summary: string }> = []
        if (fullTextStartIndex > 0) {
          for (let i = 0; i < fullTextStartIndex; i++) {
            const scene = scenes.find(s => s.id === chain.sceneIds[i])
            if (scene) {
              // chainSummaryがあればそれを使用、なければ通常のsummaryかタイトルだけ
              const summary = scene.result.chainSummary?.condensedSummary || 
                            scene.result.summary || 
                            `${scene.result.title}での出来事`
              
              summarizedScenes.push({
                id: scene.id,
                title: scene.result.title,
                summary
              })
            }
          }
        }
        
        // コンテキストテキストの生成
        let contextText = ''
        
        // 要約されたシーンがある場合
        if (summarizedScenes.length > 0) {
          contextText += '【これまでのあらすじ】\n'
          summarizedScenes.forEach((scene, index) => {
            contextText += `${index + 1}. ${scene.title}: ${scene.summary}\n`
          })
          contextText += '\n---\n\n'
        }
        
        // フルテキストシーンを連結
        if (fullScenes.length > 0) {
          contextText += '【直前のシーン】\n'
          fullScenes.forEach((scene, index) => {
            if (index === fullScenes.length - 1) {
              // 最後（現在）のシーンは含まない
              return
            }
            contextText += `\n◆ ${scene.result.title}\n\n`
            contextText += scene.result.fullText
            contextText += '\n\n---\n'
          })
        }
        
        return {
          fullScenes,
          summarizedScenes,
          contextText
        }
      },
      
      // 章セクション管理（長編対応）
      createChapterSection: (params) => {
        const { chapterTitle, chapterNumber, targetLength, sectionCount, plotId, plotChapterNumber, workId: passedWorkId, cumulativeSummary: passedCumulativeSummary } = params
        const sectionLength = Math.ceil(targetLength / sectionCount)
        
        const sections: SectionInfo[] = Array.from({ length: sectionCount }, (_, i) => ({
          sectionNumber: i + 1,
          status: 'pending' as const,
          targetLength: sectionLength,
        }))
        
        // 前章の情報を取得（同じplotIdまたはworkIdの章のみ）
        const allChapters = get().chapterSections
        
        // まず、同じ作品の前章を見つける
        let workIdToUse: string | undefined = plotId || passedWorkId
        
        if (!workIdToUse) {
          // plotIdもpassedWorkIdもない場合、直前の章番号から探す
          const prevChapter = allChapters.find(c => c.chapterNumber === chapterNumber - 1)
          if (prevChapter) {
            // 前章が見つかった場合、そのworkIdかplotIdを使用
            workIdToUse = prevChapter.workId || prevChapter.plotId
          }
        }
        
        // 関連する章をフィルタリング
        const relevantChapters = workIdToUse 
          ? allChapters.filter(ch => {
              // plotIdまたはworkIdで同じ作品の章を判定
              if (plotId && ch.plotId === plotId) return true
              if (workIdToUse && (ch.workId === workIdToUse || ch.plotId === workIdToUse)) return true
              return false
            })
          : [] // workIdが特定できない場合は、関連章なしとする
        
        const previousChapter = relevantChapters
          .filter(ch => ch.chapterNumber === chapterNumber - 1)
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0]
        
        let previousChapterFullText = ''
        let previousChapterConnection = ''
        let previousChapterHints = ''
        
        if (previousChapter) {
          // 前章の最終セクションのテキストを取得
          const lastSection = previousChapter.sections
            .filter(s => s.status === 'completed')
            .sort((a, b) => b.sectionNumber - a.sectionNumber)[0]
          
          console.log('【章作成時の引き継ぎ】前章の情報:', {
            previousChapterNumber: previousChapter.chapterNumber,
            totalSections: previousChapter.sections.length,
            completedSections: previousChapter.sections.filter(s => s.status === 'completed').length,
            lastSectionNumber: lastSection?.sectionNumber,
            hasFullText: !!lastSection?.fullText,
            hasSceneId: !!lastSection?.sceneId
          })
          
          if (lastSection) {
            if (lastSection.fullText) {
              previousChapterFullText = lastSection.fullText
              console.log('【章作成時の引き継ぎ】fullTextから取得:', previousChapterFullText.substring(0, 100))
            } else if (lastSection.sceneId) {
              const scene = get().getScene(lastSection.sceneId)
              if (scene) {
                previousChapterFullText = scene.result.fullText
                console.log('【章作成時の引き継ぎ】sceneIdから取得:', previousChapterFullText.substring(0, 100))
              }
            }
          }
          
          // 前章の要約情報から引き継ぎ情報を取得
          previousChapterConnection = previousChapter.previousChapterConnection || ''
          previousChapterHints = previousChapter.nextChapterHints || ''
        } else {
          console.log('【章作成時の引き継ぎ】前章が見つかりません。第1章の作成です。')
        }
        
        // workIdを決定（優先順位: passedWorkId > plotId > 前章のworkId > 新規生成）
        let workId: string | undefined = passedWorkId || plotId
        if (!workId && previousChapter?.workId) {
          workId = previousChapter.workId
        } else if (!workId && chapterNumber === 1) {
          // 第1章で、plotIdもworkIdもない場合は新しいworkIdを生成
          workId = `work_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
        } else if (!workId && chapterNumber > 1) {
          // 第2章以降でworkIdが見つからない場合も新規生成（別の作品として扱う）
          workId = `work_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
        }
        
        // 累積要約を生成（外部から渡された場合はそれを使用）
        const cumulativeSummary = passedCumulativeSummary || get().getCumulativeSummary(chapterNumber, workId)
        const enhancedCumulativeSummary = cumulativeSummary + 
          (previousChapterHints ? `\n\n【前章からの伏線・布石】\n${previousChapterHints}` : '')
        
        const newChapter: ChapterSection = {
          id: `chapter_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          chapterTitle,
          chapterNumber,
          plotId,
          plotChapterNumber,
          workId,
          targetLength,
          sections,
          previousChaptersSummary: cumulativeSummary,  // 標準の累積要約
          previousChapterFullText,  // 前章最終セクションの本文
          cumulativeSummary: enhancedCumulativeSummary,  // 伏線含む累積要約
          previousChapterConnection,  // 前章との繋がり
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        
        console.log('【章作成】新しい章:', {
          id: newChapter.id,
          title: newChapter.chapterTitle,
          number: newChapter.chapterNumber,
          workId: newChapter.workId,
          plotId: newChapter.plotId,
          sectionCount: newChapter.sections.length,
          作成前の章数: get().chapterSections.length
        })
        
        set(state => {
          const newChapterSections = [...state.chapterSections, newChapter]
          console.log('【章作成】保存後の章数:', newChapterSections.length)
          console.log('【章作成】全章リスト:', newChapterSections.map(ch => ({
            id: ch.id,
            title: ch.chapterTitle,
            number: ch.chapterNumber,
            workId: ch.workId
          })))
          return { chapterSections: newChapterSections }
        })
        
        return newChapter.id
      },
      
      updateSectionStatus: (chapterId, sectionNumber, status, sceneId) => {
        console.log('【セクション更新】開始:', { 
          chapterId, 
          sectionNumber, 
          status, 
          sceneId,
          現在の章数: get().chapterSections.length
        })
        
        set(state => {
          const updatedChapters = state.chapterSections.map(chapter => {
            if (chapter.id !== chapterId) return chapter
            
            const scene = sceneId ? get().getScene(sceneId) : undefined
            const actualLength = scene?.result.wordCount
            
            // completedステータス時は本文とタイトルを保存
            const fullText = status === 'completed' && scene ? scene.result.fullText : undefined
            const title = status === 'completed' && scene ? scene.result.title : undefined
            
            console.log('【セクション更新】詳細:', {
              章タイトル: chapter.chapterTitle,
              章番号: chapter.chapterNumber,
              更新前sections: chapter.sections.map(s => ({ num: s.sectionNumber, status: s.status })),
              fullText長さ: fullText?.length,
              title: title
            })
            
            const updatedChapter = {
              ...chapter,
              sections: chapter.sections.map(section => {
                if (section.sectionNumber !== sectionNumber) return section
                
                // revision→in_progressの場合は既存のデータを保持
                if (status === 'in_progress' && section.status === 'revision') {
                  console.log('【セクション更新】revision→in_progress: 既存データ保持', {
                    既存sceneId: section.sceneId,
                    既存fullText長さ: section.fullText?.length,
                    既存title: section.title
                  })
                  return {
                    ...section,
                    status,
                    // sceneIdが渡されていない場合は既存のものを保持
                    sceneId: sceneId || section.sceneId,
                    // actualLengthも既存のものを保持
                    actualLength: actualLength || section.actualLength
                    // fullTextとtitleは既存のものがそのまま保持される
                  }
                }
                
                // completed→revisionの場合も既存のデータを保持
                if (status === 'revision' && section.status === 'completed') {
                  console.log('【セクション更新】completed→revision: 既存データ保持', {
                    既存sceneId: section.sceneId,
                    既存fullText長さ: section.fullText?.length
                  })
                  return {
                    ...section,
                    status
                    // sceneId、fullText、titleは既存のものがそのまま保持される
                  }
                }
                
                // その他の場合（通常のcompleted更新など）
                return {
                  ...section,
                  status,
                  sceneId: sceneId !== undefined ? sceneId : section.sceneId, // undefinedの場合は既存値を保持
                  actualLength: actualLength !== undefined ? actualLength : section.actualLength,
                  ...(fullText && { fullText }),
                  ...(title && { title })
                }
              }),
              updatedAt: new Date().toISOString()
            }
            
            console.log('【セクション更新】更新後sections:', 
              updatedChapter.sections.map(s => ({ 
                num: s.sectionNumber, 
                status: s.status,
                hasFullText: !!s.fullText
              }))
            )
            
            return updatedChapter
          })
          
          console.log('【セクション更新】完了:', {
            更新後の章数: updatedChapters.length,
            全章の概要: updatedChapters.map(ch => ({
              id: ch.id,
              title: ch.chapterTitle,
              number: ch.chapterNumber,
              セクション数: ch.sections.length,
              完了セクション数: ch.sections.filter(s => s.status === 'completed').length
            }))
          })
          
          return { chapterSections: updatedChapters }
        })
      },
      
      updateSectionNotes: (chapterId, sectionNumber, notes) => {
        set(state => ({
          chapterSections: state.chapterSections.map(chapter => {
            if (chapter.id !== chapterId) return chapter
            
            return {
              ...chapter,
              sections: chapter.sections.map(section => {
                if (section.sectionNumber !== sectionNumber) return section
                return { ...section, notes }
              }),
              updatedAt: new Date().toISOString()
            }
          })
        }))
      },
      
      updateSectionSummary: (chapterId, sectionNumber, summary) => {
        set(state => ({
          chapterSections: state.chapterSections.map(chapter => {
            if (chapter.id !== chapterId) return chapter
            
            return {
              ...chapter,
              sections: chapter.sections.map(section => {
                if (section.sectionNumber !== sectionNumber) return section
                return { ...section, summary }
              }),
              updatedAt: new Date().toISOString()
            }
          })
        }))
      },
      
      updateSectionTargetLength: (chapterId, sectionNumber, targetLength) => {
        set(state => ({
          chapterSections: state.chapterSections.map(chapter => {
            if (chapter.id !== chapterId) return chapter
            
            return {
              ...chapter,
              sections: chapter.sections.map(section => {
                if (section.sectionNumber !== sectionNumber) return section
                return { ...section, targetLength }
              }),
              updatedAt: new Date().toISOString()
            }
          })
        }))
      },
      
      updateChapterAvoidWords: (chapterId, avoidWords) => {
        set(state => ({
          chapterSections: state.chapterSections.map(chapter => {
            if (chapter.id !== chapterId) return chapter
            
            return {
              ...chapter,
              avoidWords,
              updatedAt: new Date().toISOString()
            }
          })
        }))
      },
      
      getChapterSection: (chapterId) => {
        return get().chapterSections.find(chapter => chapter.id === chapterId)
      },
      
      getNextPendingSection: (chapterId) => {
        const chapter = get().getChapterSection(chapterId)
        if (!chapter) return undefined
        return chapter.sections.find(section => section.status === 'pending')
      },
      
      getCurrentOrNextSection: (chapterId) => {
        const chapter = get().getChapterSection(chapterId)
        if (!chapter) return undefined
        // 優先順位: in_progress > revision > pending
        // まずin_progressのセクションを探す
        const inProgressSection = chapter.sections.find(section => section.status === 'in_progress')
        if (inProgressSection) return inProgressSection
        // 次にrevision（書き直し中）のセクションを探す
        const revisionSection = chapter.sections.find(section => section.status === 'revision')
        if (revisionSection) return revisionSection
        // なければpendingの最初のセクションを返す
        return chapter.sections.find(section => section.status === 'pending')
      },
      
      deleteChapterSection: (chapterId) => {
        set(state => ({
          chapterSections: state.chapterSections.filter(chapter => chapter.id !== chapterId)
        }))
      },
      
      // 章要約機能の実装
      generateChapterSummary: async (chapterId) => {
        const chapter = get().getChapterSection(chapterId)
        if (!chapter) return
        
        // すべてのセクションのテキストを取得（fullTextがあればそれを使用）
        const sectionTexts: string[] = []
        let sectionsWithText = 0
        let sectionsFromFullText = 0
        let sectionsFromScene = 0
        
        for (const section of chapter.sections) {
          if (section.fullText) {
            // セクションに直接保存されたテキストを使用
            sectionTexts.push(section.fullText)
            sectionsWithText++
            sectionsFromFullText++
            console.log(`【章要約】セクション${section.sectionNumber}: fullTextから取得 (${section.fullText.length}文字)`)
          } else if (section.sceneId) {
            // シーンIDから取得
            const scene = get().getScene(section.sceneId)
            if (scene) {
              sectionTexts.push(scene.result.fullText)
              sectionsWithText++
              sectionsFromScene++
              console.log(`【章要約】セクション${section.sectionNumber}: sceneから取得 (${scene.result.fullText.length}文字)`)
            } else {
              console.warn(`【章要約】セクション${section.sectionNumber}: sceneId ${section.sceneId} が見つかりません`)
            }
          } else {
            console.warn(`【章要約】セクション${section.sectionNumber}: テキストが見つかりません`)
          }
        }
        
        console.log('【章要約】テキスト取得結果:', {
          章タイトル: chapter.chapterTitle,
          章番号: chapter.chapterNumber,
          全セクション数: chapter.sections.length,
          テキスト取得セクション数: sectionsWithText,
          fullTextから: sectionsFromFullText,
          sceneから: sectionsFromScene,
          合計文字数: sectionTexts.join('').length
        })
        
        if (sectionTexts.length === 0) {
          console.error('章要約生成エラー: セクションのテキストが見つかりません')
          return
        }
        
        // 章全体のテキストを結合
        const fullChapterText = sectionTexts.join('\n\n')
        
        // APIキーを取得
        const activeApiKey = useSettingsStore.getState().activeApiKey
        if (!activeApiKey) {
          console.error('APIキーが設定されていません')
          throw new Error('APIキーが設定されていません')
        }
        const apiKey = activeApiKey.key
        
        try {
          // 前章までの累積要約を取得（同じ作品の章のみ）
          const workId = chapter.workId || chapter.plotId
          console.log('【章要約】累積要約取得:', {
            現在の章番号: chapter.chapterNumber,
            workId: workId,
            plotId: chapter.plotId
          })
          
          const previousChaptersSummary = get().getCumulativeSummary(chapter.chapterNumber, workId)
          console.log('【章要約】累積要約の内容:', {
            文字数: previousChaptersSummary.length,
            先頭100文字: previousChaptersSummary.substring(0, 100)
          })
          
          // プロットコンテキストを取得（TODO: plotStoreから取得）
          const plotContext = chapter.plotId ? '' : '' // 後で実装
          
          // API呼び出し
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787'
          const response = await fetch(`${apiUrl}/api/v1/generate/chapter-summary`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-API-Key': apiKey
            },
            body: JSON.stringify({
              chapterNumber: chapter.chapterNumber,
              chapterTitle: chapter.chapterTitle,
              chapterFullText: fullChapterText,
              previousChaptersSummary,
              plotContext,
              worldContext: '', // TODO: 世界観コンテキストを追加
              useProMode: false, // TODO: ProModeの設定を反映
              modelId: getCurrentGeneratorModel('scene')
            })
          })
          
          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.message || '章要約の生成に失敗しました')
          }
          
          const data = await response.json()
          const result = data.result
          
          // ChapterSummaryの形式に変換
          const summary: ChapterSummary = {
            detailedSummary: result.detailedSummary,
            keyEvents: result.keyEvents || [],
            characterDevelopments: result.characterDevelopments || [],
            plotProgressions: result.plotProgressions || [],
            unresolved: result.unresolved || [],
            worldBuilding: result.worldBuilding || [],
            emotionalBeats: result.emotionalBeats || [],
            generatedAt: new Date().toISOString()
          }
          
          // 章要約を保存
          get().updateChapterSummary(chapterId, summary)
          
          // 次章のためのデータを保存（previousChapterConnection, nextChapterHints）
          if (result.previousChapterConnection || result.nextChapterHints) {
            set(state => ({
              chapterSections: state.chapterSections.map(ch => {
                if (ch.id !== chapterId) return ch
                return {
                  ...ch,
                  previousChapterConnection: result.previousChapterConnection,
                  nextChapterHints: result.nextChapterHints
                }
              })
            }))
          }
        } catch (error: any) {
          console.error('章要約生成エラー:', error)
          throw error
        }
      },
      
      resetChapterSummary: (chapterId) => {
        set(state => ({
          chapterSections: state.chapterSections.map(chapter => {
            if (chapter.id !== chapterId) return chapter
            return {
              ...chapter,
              chapterSummary: undefined,
              previousChapterConnection: undefined,
              nextChapterHints: undefined,
              updatedAt: new Date().toISOString()
            }
          })
        }))
      },
      
      updateChapterSummary: (chapterId, summary) => {
        set(state => ({
          chapterSections: state.chapterSections.map(chapter => {
            if (chapter.id !== chapterId) return chapter
            return {
              ...chapter,
              chapterSummary: summary,
              isCompleted: true,  // 章要約が生成されたら完了とみなす
              updatedAt: new Date().toISOString()
            }
          })
        }))
      },
      
      updatePreviousChaptersSummary: (chapterId, cumulativeSummary) => {
        set(state => ({
          chapterSections: state.chapterSections.map(chapter => {
            if (chapter.id !== chapterId) return chapter
            return {
              ...chapter,
              previousChaptersSummary: cumulativeSummary,
              updatedAt: new Date().toISOString()
            }
          })
        }))
      },
      
      markChapterAsCompleted: (chapterId) => {
        set(state => ({
          chapterSections: state.chapterSections.map(chapter => {
            if (chapter.id !== chapterId) return chapter
            
            // すべてのセクションが完了しているか確認
            const allCompleted = chapter.sections.every(s => s.status === 'completed')
            
            return {
              ...chapter,
              isCompleted: allCompleted,
              updatedAt: new Date().toISOString()
            }
          })
        }))
      },
      
      getCompletedChapters: (workId?: string) => {
        let chapters = get().chapterSections
          .filter(chapter => {
            // isCompletedフラグがある場合はそれを使用
            if (chapter.isCompleted !== undefined) {
              return chapter.isCompleted
            }
            // ない場合は全セクションが完了しているかチェック（インポートされた章対応）
            return chapter.sections.every(s => s.status === 'completed')
          })
        
        // workIdが指定されている場合は、同じ作品の章のみを取得
        if (workId) {
          chapters = chapters.filter(ch => ch.workId === workId || ch.plotId === workId)
        }
        
        return chapters.sort((a, b) => a.chapterNumber - b.chapterNumber)
      },
      
      getCumulativeSummary: (upToChapter, workId?: string) => {
        const completedChapters = get().getCompletedChapters(workId)
          .filter(ch => ch.chapterNumber < upToChapter)
        
        console.log('【累積要約】取得対象の完了済み章:', {
          対象章番号: upToChapter,
          workId: workId,
          完了済み章数: completedChapters.length,
          章番号リスト: completedChapters.map(ch => ch.chapterNumber)
        })
        
        if (completedChapters.length === 0) {
          // 完了済み章がない場合でも、インポートされた章の累積要約があれば使用
          const allChapters = get().chapterSections
            .filter(ch => ch.chapterNumber < upToChapter)
            .filter(ch => workId ? (ch.workId === workId || ch.plotId === workId) : true)
            .sort((a, b) => a.chapterNumber - b.chapterNumber)
          
          console.log('【累積要約】完了章なし - 全章から検索:', {
            全章数: allChapters.length,
            章番号リスト: allChapters.map(ch => ch.chapterNumber)
          })
          
          // インポートされた章に累積要約が含まれている場合
          if (allChapters.length > 0) {
            const lastChapter = allChapters[allChapters.length - 1]
            if (lastChapter.cumulativeSummary) {
              console.log('【累積要約】インポートされた章の累積要約を使用')
              return lastChapter.cumulativeSummary
            }
            // または前章までの要約があれば
            if (lastChapter.previousChaptersSummary) {
              console.log('【累積要約】インポートされた章の前章要約を使用')
              return lastChapter.previousChaptersSummary
            }
            // 章要約がある場合はそれを使用
            if (lastChapter.chapterSummary) {
              console.log('【累積要約】インポートされた章の章要約を使用')
              return `【第${lastChapter.chapterNumber}章: ${lastChapter.chapterTitle}】\n${lastChapter.chapterSummary.detailedSummary}`
            }
          }
          
          return ''
        }
        
        // 各章の要約を結合して累積要約を作成
        const summaries = completedChapters
          .map(ch => {
            if (!ch.chapterSummary) {
              // 章要約がない場合、セクションの内容から簡易要約を生成
              const completedSections = ch.sections.filter(s => s.status === 'completed' && s.fullText)
              if (completedSections.length > 0) {
                // セクションのテキストから簡易要約を作成（最初の500文字）
                const sectionTexts = completedSections
                  .map(s => s.fullText || '')
                  .join('\n')
                  .substring(0, 500)
                return `【第${ch.chapterNumber}章: ${ch.chapterTitle}】\n${sectionTexts}...（セクション${completedSections.length}個完了）`
              }
              return ''
            }
            return `【第${ch.chapterNumber}章: ${ch.chapterTitle}】\n${ch.chapterSummary.detailedSummary}`
          })
          .filter(Boolean)
          .join('\n\n')
        
        console.log('【累積要約】生成完了:', {
          要約文字数: summaries.length,
          含まれる章数: completedChapters.length
        })
        
        return summaries
      },

      // 検索・フィルタ
      searchScenes: (query) => {
        const lowerQuery = query.toLowerCase()
        return get().scenes.filter(scene =>
          scene.result.title.toLowerCase().includes(lowerQuery) ||
          scene.result.fullText.toLowerCase().includes(lowerQuery) ||
          scene.result.summary?.toLowerCase().includes(lowerQuery)
        )
      },

      filterByCharacter: (characterId) => {
        return get().scenes.filter(scene =>
          scene.result.metadata.linkedElements?.characterIds?.includes(characterId)
        )
      },

      filterByLocation: (location) => {
        const lowerLocation = location.toLowerCase()
        return get().scenes.filter(scene =>
          scene.input.sceneLocation?.toLowerCase().includes(lowerLocation)
        )
      },

      filterByWorld: (worldId) => {
        return get().scenes.filter(scene =>
          scene.result.metadata.linkedElements?.worldId === worldId
        )
      },

      // エクスポート・インポート
      exportAsText: () => {
        const { scenes } = get()
        const text = scenes.map(scene => {
          const divider = '='.repeat(80)
          return `${divider}
タイトル: ${scene.result.title}
生成日時: ${new Date(scene.timestamp).toLocaleString('ja-JP')}
文字数: ${scene.result.wordCount}文字
読了時間: ${scene.result.readingTime}
${scene.result.summary ? `\n要約: ${scene.result.summary}\n` : ''}
${divider}

${scene.result.fullText}

`
        }).join('\n\n')

        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `scenes_${new Date().toISOString().split('T')[0]}.txt`
        a.click()
        URL.revokeObjectURL(url)
      },

      exportAsJSON: () => {
        const { scenes, sceneChains } = get()
        const data = {
          version: '1.0',
          exportDate: new Date().toISOString(),
          scenes,
          sceneChains
        }

        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `scenes_${new Date().toISOString().split('T')[0]}.json`
        a.click()
        URL.revokeObjectURL(url)
      },

      exportSingleAsText: (id) => {
        const scene = get().scenes.find(s => s.id === id)
        if (!scene) return

        const divider = '='.repeat(80)
        const text = `${divider}
タイトル: ${scene.result.title}
生成日時: ${new Date(scene.timestamp).toLocaleString('ja-JP')}
文字数: ${scene.result.wordCount}文字
読了時間: ${scene.result.readingTime}
${scene.result.summary ? `\n要約: ${scene.result.summary}\n` : ''}
${divider}

${scene.result.fullText}
`

        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${scene.result.title}_${new Date().toISOString().split('T')[0]}.txt`
        a.click()
        URL.revokeObjectURL(url)
      },

      exportSingleAsJSON: (id) => {
        const scene = get().scenes.find(s => s.id === id)
        if (!scene) return

        const data = {
          version: '1.0',
          exportDate: new Date().toISOString(),
          scene
        }

        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${scene.result.title}_${new Date().toISOString().split('T')[0]}.json`
        a.click()
        URL.revokeObjectURL(url)
      },

      exportChainAsText: (chainId) => {
        const { scenes, sceneChains } = get()
        const chain = sceneChains.find(c => c.id === chainId)
        if (!chain) return

        const chainScenes = chain.sceneIds
          .map(id => scenes.find(s => s.id === id))
          .filter(Boolean) as SavedScene[]

        const text = `# ${chain.name}\n\n` + chainScenes.map((scene, index) => {
          const divider = '-'.repeat(60)
          return `${divider}
第${index + 1}章: ${scene.result.title}

${scene.result.fullText}

`
        }).join('\n')

        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${chain.name}_${new Date().toISOString().split('T')[0]}.txt`
        a.click()
        URL.revokeObjectURL(url)
      },

      exportChapterAsText: (chapterId) => {
        const chapter = get().getChapterSection(chapterId)
        if (!chapter) return
        
        // 章の全セクションのテキストを取得
        const sectionTexts: Array<{ number: number; title: string; text: string }> = []
        
        for (const section of chapter.sections) {
          let text = ''
          let title = ''
          
          // まずセクションに保存されたテキストを使用
          if (section.fullText) {
            text = section.fullText
            title = section.title || `セクション${section.sectionNumber}`
          } else if (section.sceneId) {
            // なければシーンから取得
            const scene = get().getScene(section.sceneId)
            if (scene) {
              text = scene.result.fullText
              title = scene.result.title
            }
          }
          
          if (text) {
            sectionTexts.push({
              number: section.sectionNumber,
              title,
              text
            })
          }
        }
        
        // テキストファイルの生成
        let content = `${'='.repeat(80)}\n`
        content += `第${chapter.chapterNumber}章: ${chapter.chapterTitle}\n`
        content += `${'='.repeat(80)}\n\n`
        
        // 章要約があれば追加
        if (chapter.chapterSummary) {
          content += `【章要約】\n${chapter.chapterSummary.detailedSummary}\n\n`
          
          if (chapter.chapterSummary.keyEvents.length > 0) {
            content += `【重要な出来事】\n`
            chapter.chapterSummary.keyEvents.forEach(event => {
              content += `・${event}\n`
            })
            content += '\n'
          }
          
          if (chapter.chapterSummary.unresolved.length > 0) {
            content += `【未解決の伏線】\n`
            chapter.chapterSummary.unresolved.forEach(item => {
              content += `・${item}\n`
            })
            content += '\n'
          }
          
          content += `${'-'.repeat(80)}\n\n`
        }
        
        // 各セクションのテキストを追加
        sectionTexts.forEach(section => {
          content += `${'-'.repeat(60)}\n`
          content += `セクション${section.number}: ${section.title}\n`
          content += `${'-'.repeat(60)}\n\n`
          content += section.text
          content += '\n\n'
        })
        
        // ファイルのダウンロード
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `第${chapter.chapterNumber}章_${chapter.chapterTitle}_${new Date().toISOString().split('T')[0]}.txt`
        a.click()
        URL.revokeObjectURL(url)
      },

      exportChapterAsJSON: (chapterId) => {
        const chapter = get().getChapterSection(chapterId)
        if (!chapter) return
        
        // 関連するシーンデータも収集
        const relatedScenes: SavedScene[] = []
        
        // 章の全データを収集
        const chapterData = {
          ...chapter,
          sections: chapter.sections.map(section => {
            // セクションの完全なデータを保存
            const sectionData: any = { ...section }
            
            // テキストとタイトルが未保存の場合はシーンから取得
            if (!sectionData.fullText && section.sceneId) {
              const scene = get().getScene(section.sceneId)
              if (scene) {
                sectionData.fullText = scene.result.fullText
                sectionData.title = scene.result.title
                // シーンデータも保存対象に追加
                relatedScenes.push(scene)
              }
            } else if (section.sceneId) {
              // fullTextがあってもシーンがあれば保存
              const scene = get().getScene(section.sceneId)
              if (scene) {
                relatedScenes.push(scene)
              }
            }
            
            return sectionData
          })
        }
        
        const exportData = {
          version: '2.0',  // バージョンアップ
          exportDate: new Date().toISOString(),
          type: 'chapter',
          chapter: chapterData,
          scenes: relatedScenes  // 関連シーンデータも含める
        }
        
        // JSONファイルのダウンロード
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `第${chapter.chapterNumber}章_${chapter.chapterTitle}_${new Date().toISOString().split('T')[0]}.json`
        a.click()
        URL.revokeObjectURL(url)
      },

      importFromJSON: (jsonString) => {
        try {
          console.log('[SceneStore] importFromJSON開始')
          console.log('[SceneStore] 受信JSON長さ:', jsonString.length)
          const importData = JSON.parse(jsonString)
          console.log('[SceneStore] パース後データ:', importData)
          
          // 複数の形式をサポート
          let scenes: SavedScene[] = []
          if (importData.data && Array.isArray(importData.data)) {
            // BulkDataManager形式
            console.log('[SceneStore] BulkDataManager形式検出: data.data')
            scenes = importData.data
          } else if (importData.scenes && Array.isArray(importData.scenes)) {
            // 個別エクスポート形式
            console.log('[SceneStore] 個別エクスポート形式検出: scenes')
            scenes = importData.scenes
          } else if (Array.isArray(importData)) {
            // 直接配列形式
            console.log('[SceneStore] 直接配列形式検出')
            scenes = importData
          } else {
            console.error('[SceneStore] 不明なデータ形式:', importData)
            throw new Error('Invalid JSON format')
          }
          console.log('[SceneStore] 抽出されたシーン数:', scenes.length)

          const currentScenes = get().scenes
          const currentChains = get().sceneChains
          
          // シーンの重複チェック（タイトルとフルテキストで判定）
          const uniqueScenes = scenes.filter((newScene: any) => {
            return !currentScenes.some(existing => 
              existing.result.title === newScene.result.title && 
              existing.result.fullText === newScene.result.fullText
            )
          })

          const importedScenes = uniqueScenes.map((scene: any, index: number) => ({
            ...scene,
            id: `imported_${Date.now() + index}_${Math.random().toString(36).slice(2, 11)}`,
            timestamp: Date.now()
          }))

          // チェーンの重複チェック（名前で判定）
          const uniqueChains = ((importData.sceneChains || importData.chains || [])).filter((newChain: any) => {
            return !currentChains.some(existing => 
              existing.name === newChain.name
            )
          })

          const importedChains = uniqueChains.map((chain: any, index: number) => ({
            ...chain,
            id: `imported_chain_${Date.now() + index}_${Math.random().toString(36).slice(2, 11)}`
          }))

          console.log('[SceneStore] インポート予定シーン数:', importedScenes.length)
          console.log('[SceneStore] インポート予定チェーン数:', importedChains.length)
          
          set(state => {
            let scenes = [...importedScenes, ...state.scenes]
            if (scenes.length > MAX_SCENES) {
              scenes = scenes.slice(0, MAX_SCENES)
            }
            return {
              scenes,
              sceneChains: [...importedChains, ...state.sceneChains]
            }
          })
          console.log('[SceneStore] set完了')

          const skippedScenes = scenes.length - uniqueScenes.length
          const skippedChains = ((importData.sceneChains || importData.chains || []).length) - uniqueChains.length
          
          let message = `${importedScenes.length}個のシーンと${importedChains.length}個のチェーンをインポートしました`
          if (skippedScenes > 0 || skippedChains > 0) {
            message += `（${skippedScenes}個のシーン、${skippedChains}個のチェーンの重複をスキップ）`
          }
          console.log('[SceneStore] アラート表示:', message)
          alert(message)
          console.log(`[SceneStore] Imported ${importedScenes.length} unique scenes and ${importedChains.length} unique chains (${skippedScenes} scenes and ${skippedChains} chains skipped)`)
        } catch (error) {
          console.error('[SceneStore] インポートエラー:', error)
          console.error('[SceneStore] エラースタック:', (error as Error).stack)
          alert('インポートに失敗しました。ファイル形式を確認してください。')
          throw error
        }
      },

      importChapterFromJSON: (jsonString) => {
        try {
          const data = JSON.parse(jsonString)
          
          // 章データの検証
          if (!data.type || data.type !== 'chapter' || !data.chapter) {
            throw new Error('章データではありません')
          }
          
          const importedChapter = data.chapter
          const importedScenes = data.scenes || []  // v2.0形式の場合
          const existingChapters = get().chapterSections
          
          console.log('【章インポート】データ確認:', {
            バージョン: data.version,
            章タイトル: importedChapter.chapterTitle,
            章番号: importedChapter.chapterNumber,
            セクション数: importedChapter.sections?.length,
            シーン数: importedScenes.length
          })
          
          // 同じ章番号・タイトルの章が存在するかチェック
          const existingChapter = existingChapters.find(ch => 
            ch.chapterNumber === importedChapter.chapterNumber &&
            ch.chapterTitle === importedChapter.chapterTitle
          )
          
          if (existingChapter) {
            if (!confirm(`第${importedChapter.chapterNumber}章「${importedChapter.chapterTitle}」は既に存在します。上書きしますか？`)) {
              return false
            }
            
            // 既存の章を削除
            set(state => ({
              chapterSections: state.chapterSections.filter(ch => ch.id !== existingChapter.id)
            }))
          }
          
          // 新しいIDを生成（インポート時は新しいIDを使う）
          const newChapter = {
            ...importedChapter,
            id: `imported_chapter_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
            updatedAt: new Date().toISOString(),
            sections: importedChapter.sections.map((section: any) => ({
              ...section,
              // fullTextとtitleが保存されていることを確認
              fullText: section.fullText || '',
              title: section.title || `セクション${section.sectionNumber}`
            })),
            // 章の完了状態やworkIDを保持
            isCompleted: importedChapter.isCompleted !== undefined ? importedChapter.isCompleted : 
                        importedChapter.sections?.every((s: any) => s.status === 'completed'),
            workId: importedChapter.workId || importedChapter.plotId, // workIdまたはplotIdを保持
            plotId: importedChapter.plotId,
            chapterSummary: importedChapter.chapterSummary || null,
            previousChaptersSummary: importedChapter.previousChaptersSummary || '',
            cumulativeSummary: importedChapter.cumulativeSummary || ''
          }
          
          console.log('【章インポート】インポートする章:', {
            id: newChapter.id,
            title: newChapter.chapterTitle,
            number: newChapter.chapterNumber,
            sectionCount: newChapter.sections.length,
            completedSections: newChapter.sections.filter((s: any) => s.status === 'completed').length
          })
          
          // シーンデータも復元（v2.0形式の場合）
          if (importedScenes.length > 0) {
            const sceneIdMapping: { [oldId: string]: string } = {}
            const newScenes: SavedScene[] = []
            
            // シーンのIDをマッピング（新しいIDを生成）
            importedScenes.forEach(scene => {
              const newSceneId = `imported_scene_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
              sceneIdMapping[scene.id] = newSceneId
              newScenes.push({
                ...scene,
                id: newSceneId,
                timestamp: Date.now()
              })
            })
            
            // セクションのsceneIdを新しいIDに更新
            newChapter.sections = newChapter.sections.map(section => {
              if (section.sceneId && sceneIdMapping[section.sceneId]) {
                return {
                  ...section,
                  sceneId: sceneIdMapping[section.sceneId]
                }
              }
              return section
            })
            
            console.log('【章インポート】シーン復元:', {
              復元シーン数: newScenes.length,
              IDマッピング: Object.keys(sceneIdMapping).length
            })
            
            // シーンとチャプターを同時に追加
            set(state => {
              const newChapterSections = [...state.chapterSections, newChapter]
                .sort((a, b) => a.chapterNumber - b.chapterNumber)
              
              let scenes = [...newScenes, ...state.scenes]
              if (scenes.length > MAX_SCENES) {
                scenes = scenes.slice(0, MAX_SCENES)
              }
              
              console.log('【章インポート】インポート完了:', {
                章数: newChapterSections.length,
                シーン総数: scenes.length
              })
              
              return { 
                chapterSections: newChapterSections,
                scenes: scenes
              }
            })
          } else {
            // v1.0形式（シーンデータなし）- セクションからシーンを生成
            console.log('【章インポート】v1.0形式検出 - セクションからシーンを生成')
            
            const newScenes: SavedScene[] = []
            const timestamp = Date.now()
            
            // 各セクションからシーンを生成
            newChapter.sections = newChapter.sections.map((section, index) => {
              if (section.status === 'completed' && section.fullText) {
                // 新しいシーンIDを生成
                const newSceneId = `imported_v1_scene_${timestamp}_${index}_${Math.random().toString(36).slice(2, 11)}`
                
                // セクションのテキストからシーンを作成
                const newScene: SavedScene = {
                  id: newSceneId,
                  input: {
                    sceneSummary: `第${newChapter.chapterNumber}章 セクション${section.sectionNumber}`,
                    chapterSection: {
                      chapterId: newChapter.id,
                      sectionNumber: section.sectionNumber
                    }
                  },
                  result: {
                    title: section.title || `第${newChapter.chapterNumber}章 セクション${section.sectionNumber}`,
                    fullText: section.fullText,
                    paragraphs: [], // 擬似的なシーンなので段落分解は省略
                    wordCount: section.fullText.length,
                    readingTime: `${Math.ceil(section.fullText.length / 500)}分`,
                    summary: `第${newChapter.chapterNumber}章 セクション${section.sectionNumber}の内容`,
                    metadata: {
                      generatedAt: new Date().toISOString(),
                      mode: 'imported',
                      parameters: {
                        targetLength: section.fullText.length,
                        actualLength: section.fullText.length,
                        creativityLevel: 5
                      }
                    }
                  },
                  timestamp: timestamp + index
                }
                
                newScenes.push(newScene)
                
                console.log('【章インポート】v1.0シーン生成:', {
                  セクション番号: section.sectionNumber,
                  シーンID: newSceneId,
                  テキスト長: section.fullText.length
                })
                
                // セクションにsceneIdを追加
                return {
                  ...section,
                  sceneId: newSceneId
                }
              }
              return section
            })
            
            console.log('【章インポート】v1.0形式の変換完了:', {
              生成シーン数: newScenes.length,
              更新セクション数: newChapter.sections.filter(s => s.sceneId).length
            })
            
            set(state => {
              const newChapterSections = [...state.chapterSections, newChapter]
                .sort((a, b) => a.chapterNumber - b.chapterNumber)
              
              let scenes = [...newScenes, ...state.scenes]
              if (scenes.length > MAX_SCENES) {
                scenes = scenes.slice(0, MAX_SCENES)
              }
              
              console.log('【章インポート】v1.0インポート完了:', {
                章数: newChapterSections.length,
                シーン総数: scenes.length
              })
              
              return { 
                chapterSections: newChapterSections,
                scenes: scenes
              }
            })
          }
          
          alert(`第${importedChapter.chapterNumber}章「${importedChapter.chapterTitle}」をインポートしました`)
          return true
          
        } catch (error) {
          console.error('章インポートエラー:', error)
          alert('章のインポートに失敗しました。ファイルの形式を確認してください。')
          return false
        }
      },

      // ユーティリティ
      clearAll: () => {
        if (confirm('すべてのシーンとチェーンを削除しますか？この操作は取り消せません。')) {
          set({ scenes: [], currentScene: null, sceneChains: [] })
        }
      },

      fixDuplicateIds: () => {
        const { scenes } = get()
        const idMap = new Map<string, number>()
        const duplicates: string[] = []
        
        // 重複IDを検出
        scenes.forEach(scene => {
          const count = idMap.get(scene.id) || 0
          idMap.set(scene.id, count + 1)
          if (count > 0) {
            duplicates.push(scene.id)
          }
        })
        
        if (duplicates.length === 0) {
          return false
        }
        
        // 重複IDを修正
        set(state => ({
          scenes: state.scenes.map(scene => {
            if (duplicates.includes(scene.id)) {
              return {
                ...scene,
                id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
              }
            }
            return scene
          })
        }))
        
        return true
      }
    }),
    {
      name: 'scene-storage',
      version: 1,
      storage: createIndexedDBStorage('scene-storage'),
      partialize: (state) => ({ 
        scenes: state.scenes, 
        currentScene: state.currentScene, 
        sceneChains: state.sceneChains,
        chapterSections: state.chapterSections 
      }),
      migrate: (persistedState: any, version: number) => {
        // バージョン0（初期状態）からバージョン1への移行
        if (version === 0) {
          // 古いデータ構造から新しいデータ構造への変換
          return persistedState
        }
        return persistedState
      }
    }
  )
)
